"use strict";
/**
 * Employment Enums — HR Model Restructuring (GH10)
 *
 * New enums for the restructured Employment model.
 * All enums follow the new standard: snake_case values + LABELS objects.
 *
 * @see .claude/rules/enums.md for conventions
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPLOYMENT_TYPE_DEFAULTS = exports.ATTENDANCE_SOURCE_LABELS = exports.AttendanceSource = exports.SCHEDULE_TYPE_LABELS = exports.ScheduleType = exports.PAYROLL_STAMPING_MODE_LABELS = exports.PayrollStampingMode = exports.COMPENSATION_TYPE_LABELS = exports.CompensationType = exports.EMPLOYMENT_TYPE_LABELS = exports.EmploymentType = void 0;
// ─── Employment Type ─────────────────────────────────────────────────────────
/**
 * Classifies the nature of the employment relationship.
 *
 * Each type has a defined set of defaults for compensation, IMSS,
 * stamping mode, and schedule. See EMPLOYMENT_TYPE_DEFAULTS.
 */
var EmploymentType;
(function (EmploymentType) {
    EmploymentType["payroll"] = "payroll";
    EmploymentType["payroll_draft"] = "payroll_draft";
    EmploymentType["intern"] = "intern";
    EmploymentType["hourly_agreed"] = "hourly_agreed";
    EmploymentType["casual"] = "casual";
    EmploymentType["contractor"] = "contractor";
    EmploymentType["coverage_only"] = "coverage_only";
})(EmploymentType || (exports.EmploymentType = EmploymentType = {}));
exports.EMPLOYMENT_TYPE_LABELS = {
    [EmploymentType.payroll]: "Nómina",
    [EmploymentType.payroll_draft]: "Nómina simplificada",
    [EmploymentType.intern]: "Practicante",
    [EmploymentType.hourly_agreed]: "Horas convenidas",
    [EmploymentType.casual]: "Eventual",
    [EmploymentType.contractor]: "Contratista",
    [EmploymentType.coverage_only]: "Solo cobertura",
};
// ─── Compensation Type ───────────────────────────────────────────────────────
/**
 * How the collaborator is compensated.
 */
var CompensationType;
(function (CompensationType) {
    CompensationType["salary"] = "salary";
    CompensationType["hourly"] = "hourly";
    CompensationType["per_service"] = "per_service";
    CompensationType["none"] = "none";
})(CompensationType || (exports.CompensationType = CompensationType = {}));
exports.COMPENSATION_TYPE_LABELS = {
    [CompensationType.salary]: "Salario",
    [CompensationType.hourly]: "Por hora",
    [CompensationType.per_service]: "Por servicio",
    [CompensationType.none]: "Sin compensación",
};
// ─── Payroll Stamping Mode ───────────────────────────────────────────────────
/**
 * How the CFDI nómina is stamped for this employment.
 *
 * - ordinary: Full CFDI with real income (PAYROLL type)
 * - nominal: CFDI with minimum wage for IMSS purposes (CASUAL, COVERAGE_ONLY)
 * - none: No CFDI stamping (PAYROLL_DRAFT, INTERN, CONTRACTOR)
 */
var PayrollStampingMode;
(function (PayrollStampingMode) {
    PayrollStampingMode["ordinary"] = "ordinary";
    PayrollStampingMode["nominal"] = "nominal";
    PayrollStampingMode["none"] = "none";
})(PayrollStampingMode || (exports.PayrollStampingMode = PayrollStampingMode = {}));
exports.PAYROLL_STAMPING_MODE_LABELS = {
    [PayrollStampingMode.ordinary]: "Ordinario",
    [PayrollStampingMode.nominal]: "Nominal",
    [PayrollStampingMode.none]: "Sin timbrado",
};
// ─── Schedule Type ───────────────────────────────────────────────────────────
/**
 * What kind of schedule the employment follows.
 *
 * Also determines shift coverage: FIXED schedule = covers shifts.
 */
var ScheduleType;
(function (ScheduleType) {
    ScheduleType["fixed"] = "fixed";
    ScheduleType["flexible"] = "flexible";
    ScheduleType["on_demand"] = "on_demand";
    ScheduleType["none"] = "none";
})(ScheduleType || (exports.ScheduleType = ScheduleType = {}));
exports.SCHEDULE_TYPE_LABELS = {
    [ScheduleType.fixed]: "Fijo",
    [ScheduleType.flexible]: "Flexible",
    [ScheduleType.on_demand]: "Bajo demanda",
    [ScheduleType.none]: "Sin horario",
};
// ─── Attendance Source ────────────────────────────────────────────────────────
/**
 * Source of attendance tracking for this employment.
 *
 * Replaces backend-only HRAttendanceSource enum.
 */
var AttendanceSource;
(function (AttendanceSource) {
    AttendanceSource["attendance_records"] = "attendance_records";
    AttendanceSource["activity_register"] = "activity_register";
    AttendanceSource["none"] = "none";
})(AttendanceSource || (exports.AttendanceSource = AttendanceSource = {}));
exports.ATTENDANCE_SOURCE_LABELS = {
    [AttendanceSource.attendance_records]: "Registro de asistencia",
    [AttendanceSource.activity_register]: "Registro de actividades",
    [AttendanceSource.none]: "Sin registro",
};
exports.EMPLOYMENT_TYPE_DEFAULTS = {
    [EmploymentType.payroll]: {
        compensationType: CompensationType.salary,
        hasCommissions: true,
        hasImss: true,
        payrollStampingMode: PayrollStampingMode.ordinary,
        scheduleType: ScheduleType.fixed,
        attendanceSource: AttendanceSource.attendance_records,
    },
    [EmploymentType.payroll_draft]: {
        compensationType: CompensationType.salary,
        hasCommissions: true,
        hasImss: false,
        payrollStampingMode: PayrollStampingMode.none,
        scheduleType: ScheduleType.fixed,
        attendanceSource: AttendanceSource.attendance_records,
    },
    [EmploymentType.intern]: {
        compensationType: CompensationType.hourly,
        hasCommissions: true,
        hasImss: false,
        payrollStampingMode: PayrollStampingMode.none,
        scheduleType: ScheduleType.fixed,
        attendanceSource: AttendanceSource.attendance_records,
    },
    [EmploymentType.hourly_agreed]: {
        compensationType: CompensationType.hourly,
        hasCommissions: true,
        hasImss: false,
        payrollStampingMode: PayrollStampingMode.none,
        scheduleType: ScheduleType.fixed,
        attendanceSource: AttendanceSource.attendance_records,
    },
    [EmploymentType.casual]: {
        compensationType: CompensationType.hourly,
        hasCommissions: true,
        hasImss: false,
        payrollStampingMode: PayrollStampingMode.nominal,
        scheduleType: ScheduleType.none,
        attendanceSource: AttendanceSource.activity_register,
    },
    [EmploymentType.contractor]: {
        compensationType: CompensationType.per_service,
        hasCommissions: true,
        hasImss: false,
        payrollStampingMode: PayrollStampingMode.none,
        scheduleType: ScheduleType.none,
        attendanceSource: AttendanceSource.none,
    },
    [EmploymentType.coverage_only]: {
        compensationType: CompensationType.none,
        hasCommissions: false,
        hasImss: true,
        payrollStampingMode: PayrollStampingMode.nominal,
        scheduleType: ScheduleType.none,
        attendanceSource: AttendanceSource.none,
    },
};
